import { ReactiveMap } from "@solid-primitives/map"
import { batch, createEffect, on } from "solid-js"
import eightDirections from "../../lib/core/direction/eightDirections"
import getNextEightDirection from "../../lib/core/direction/getNextEightDirection"
import type { TileGraphNode } from "../../lib/core/engine/TileGraphNode/TileGraphNode.type"
import { isPlainObjDeeplyEqual } from "../../lib/isPlainObjDeeplyEqual"
import { immutableEmptyArray } from "../../lib/lib"
import createNodeNeighborAvgElevationListV2 from "../../lib/three/objects/TerrainMesh1V2/createNodeNeighborAvgElevationListV2"
import createSharedCachedFactory from "./createSharedCachedFactory"
import { useNodeNeighborIndexMap } from "./useNodeNeighborIndexMap"
import { useSharedTerrainDerivedFloorElevationListMemo } from "./useSharedTerrainDerivedFloorElevationListMemo"

/**
 * ## Reactive Terrain Elevation System
 * 
 * This module implements a reactive elevation system that efficiently manages terrain height data
 * across a tile-based terrain system. It provides smooth interpolation between neighboring tiles
 * and automatically updates when terrain data changes.
 * 
 * ## Architecture Overview:
 * 
 * The system uses SolidJS reactivity to create a cached map that automatically updates when
 * the underlying terrain elevation data changes. This ensures that all dependent systems
 * (terrain meshes, object positioning, detail props) stay synchronized with terrain modifications.
 * 
 * ## Key Components:
 * 
 * ### 1. NeighborElevationCollection
 * Core data structure containing elevation data for a tile and its 8 neighbors, with
 * pre-computed averages for smooth interpolation.
 * 
 * ### 2. useNodeNeighborBaseElevationReactiveMap
 * Factory function that creates a reactive map providing elevation collections for each
 * tile index. Automatically updates when terrain data changes.
 * 
 * ## Usage Patterns:
 * 
 * ### Terrain Mesh Generation
 * ```typescript
 * // In createTerrainMesh1V2Group.ts
 * const elevationMap = useNodeNeighborBaseElevationReactiveMap(engine)
 * const collection = elevationMap.get(nodeIndex)
 * const z = getTerrainMesh1Z(collection, x, y)
 * ```
 * 
 * ### Detail Prop Positioning
 * ```typescript
 * // In grass waves, boulders, etc.
 * const collection = elevationMap.get(tileIndex)
 * const elevation = getTerrainMesh1Z(collection, localX, localY)
 * ```
 * 
 * ### Object Placement
 * ```typescript
 * // In addRandomTileDetailPropPosition.ts
 * const z = getTerrainMesh1Z(baseElevationsCollection, x, y)
 * object.position.set(worldX, worldY, z)
 * ```
 * 
 * ## Performance Optimizations:
 * 
 * - **Incremental Updates**: Only recalculates collections for tiles that have changed
 * - **Neighbor Propagation**: When a tile changes, automatically updates its neighbors
 * - **Batched Operations**: Uses SolidJS batching to minimize reactive updates
 * - **Cached Computations**: Pre-computes averaged elevations for interpolation
 * - **Efficient Fallbacks**: Handles missing neighbors gracefully with diagonal fallbacks
 * 
 * ## Data Flow:
 * 
 * 1. `useSharedTerrainDerivedElevationListMemo` provides base elevation data
 * 2. `useNodeNeighborIndexMap` provides neighbor relationships
 * 3. This module combines them into interpolation-ready collections
 * 4. `getTerrainMesh1Z` uses collections for smooth elevation queries
 * 5. Terrain meshes and objects use the elevation data for positioning
 */

/**
 * Elevation data structure for a tile and its 8 neighbors.
 * 
 * Contains all data needed for smooth terrain interpolation, with pre-computed
 * averages to optimize runtime performance during mesh generation and object positioning.
 * 
 * @property base - Elevation of the center tile
 * @property list - Array of 8 neighbor elevations in eightDirections order:
 *   [N, NE, E, SE, S, SW, W, NW]. Missing neighbors use fallback values.
 * @property avgs - Array of 8 pre-computed averaged elevations for smooth interpolation.
 *   Each average combines the base elevation with the corresponding neighbor.
 */
export type NeighborElevationCollection = {
  /** Base elevation of the center tile */
  base: number
  /** Elevations of 8 neighbors in eightDirections order (length === 8) */
  list: number[]  // length === 8
  /** Pre-computed averages for interpolation (length === 8) */
  avgs: number[]  // length === 8
}

// this is a work-around for a bug
// the 3x3 grid of tiles that are updated when a tile changes
// seems to be correct in one place, but wrong in another.
const ALWAYS_REBUILD = false

const FULL_RECALC_PARTIAL_APPLY = true

/**
 * Creates a reactive map that provides elevation collections for terrain tiles.
 * 
 * This factory function creates a shared, cached reactive map that automatically updates
 * when terrain elevation data changes. The map provides `NeighborElevationCollection`
 * objects for each tile index, containing the tile's elevation and its 8 neighbors'
 * elevations with pre-computed averages for smooth interpolation.
 * 
 * ## Reactive Behavior:
 * 
 * The map automatically updates when:
 * - Terrain elevation data changes (from `useSharedTerrainDerivedElevationListMemo`)
 * - New tiles are added or removed from the terrain
 * - Individual tile elevations are modified
 * 
 * ## Performance Features:
 * 
 * - **Incremental Updates**: Only recalculates collections for changed tiles
 * - **Neighbor Propagation**: When a tile changes, its neighbors are also updated
 * - **Batched Operations**: Uses SolidJS batching to minimize reactive cascades
 * - **Shared Caching**: Multiple components can share the same map instance
 * 
 * ## Usage:
 * 
 * ```typescript
 * const elevationMap = useNodeNeighborBaseElevationReactiveMap(engine)
 * 
 * // Get elevation collection for a specific tile
 * const collection = elevationMap.get(tileIndex)
 * if (collection) {
 *   const z = getTerrainMesh1Z(collection, localX, localY)
 * }
 * ```
 * 
 * @param engine - Game engine instance providing terrain and neighbor data
 * @returns Reactive map from tile index to elevation collection
 */
export const useNodeNeighborBaseFloorElevationReactiveMap = createSharedCachedFactory((engine) => {
  const getNodeNeighborIndexMap = useNodeNeighborIndexMap(engine)
  const getDerivedElevationList = useSharedTerrainDerivedFloorElevationListMemo(engine)

  const map = new ReactiveMap<TileGraphNode['index'], NeighborElevationCollection>()

  createEffect(on(getDerivedElevationList, (eList: Float32Array, prevList: Float32Array | undefined) => {
    const len0 = eList.length
    prevList ||= immutableEmptyArray as unknown as Float32Array
    const len1 = prevList.length
    const nodeNeighborByDirectionMap = getNodeNeighborIndexMap()
    const nodeNeighborByDirectionMapValues = nodeNeighborByDirectionMap.values()

    /**
     * Creates a NeighborElevationCollection for a specific tile index.
     * 
     * Gathers elevation data for the tile and its 8 neighbors, handling missing
     * neighbors gracefully with fallback strategies. Pre-computes averaged elevations
     * for efficient interpolation during terrain mesh generation.
     * 
     * ## Neighbor Fallback Strategy:
     * 
     * For diagonal neighbors (NE, SE, SW, NW) that are missing:
     * 1. Try adjacent cardinal directions (e.g., for NE: try N, then E)
     * 2. If both adjacent directions are missing, use base elevation
     * 
     * For cardinal neighbors (N, E, S, W) that are missing:
     * - Use base elevation directly
     * 
     * @param index - Tile index to create collection for
     * @returns Complete elevation collection with base, neighbors, and averages
     */
    function make(index: TileGraphNode['index']): NeighborElevationCollection {
      const base = eList[index]

      // Build neighbor elevation list in eightDirections order: [N, NE, E, SE, S, SW, W, NW]
      const neighborBases : Array<TileGraphNode['index']> = []

      for (let dirIndex = 0; dirIndex < 8; dirIndex++) {
        const dir = eightDirections[dirIndex]
        const neighborIndex = nodeNeighborByDirectionMap.get(dir)![index]

        // Handle missing neighbors with fallback strategy
        if (neighborIndex === -1) {
          // For diagonal neighbors (odd indices: NE, SE, SW, NW)
          if (dirIndex % 2 === 1) {
            // Try first adjacent cardinal direction (e.g., NE -> N)
            const dir1 = getNextEightDirection(dir, 1)
            const neighborIndex1 = nodeNeighborByDirectionMap.get(dir1)![index]
            if (neighborIndex1 !== -1) {
              neighborBases[dirIndex] = eList[neighborIndex1]
              continue
            }

            // Try second adjacent cardinal direction (e.g., NE -> E)
            const dir2 = getNextEightDirection(dir, -1)
            const neighborIndex2 = nodeNeighborByDirectionMap.get(dir2)![index]
            if (neighborIndex2 !== -1) {
              neighborBases[dirIndex] = eList[neighborIndex2]
              continue
            }

            // Both adjacent directions missing, use base elevation
            neighborBases[dirIndex] = base
            continue
          }

          // For cardinal neighbors (even indices: N, E, S, W), use base elevation
          neighborBases[dirIndex] = base
          continue
        }

        // Neighbor exists, use its elevation
        neighborBases[dirIndex] = eList[neighborIndex]
      }

      // Pre-compute averaged elevations for smooth interpolation
      // const avgs = createNodeNeighborAvgElevationList(base, neighborBases)
      const avgs = createNodeNeighborAvgElevationListV2(base, neighborBases)

      const collection = {
        base,
        list: neighborBases,
        avgs,
      }
      return collection
    }

    // Batch all map updates to minimize reactive cascades
    batch(() => {
      // Handle terrain size changes (tiles added/removed)
      if (ALWAYS_REBUILD) {
        // Complete rebuild - clear map and recreate all collections
        map.clear()
        for (let i = 0; i < len0; i++) {
          map.set(i, make(i))
        }
      } else if (FULL_RECALC_PARTIAL_APPLY) {
        // clear only on size change
        if (len0 !== len1) {
          map.clear()
        }
        for (let i = 0; i < len0; i++) {
          // always recreate
          const newValue = make(i)
          const oldValue = map.get(i)
          // only trigger signal if different
          if (!isPlainObjDeeplyEqual(newValue, oldValue)) {
            map.set(i, newValue)
          }
        }
      } else {
        // Incremental update - only update changed tiles and their neighbors
        const indexesToUpdate = new Set<TileGraphNode['index']>()

        // Find tiles with changed elevations
        for (let centerIndex = 0; centerIndex < len0; centerIndex++) {
          const elevation0 = prevList[centerIndex]
          const elevation1 = eList[centerIndex]
          if (elevation0 !== elevation1) {
            // Mark the changed tile for update
            indexesToUpdate.add(centerIndex)

            // Mark all neighbors for update since they reference this tile
            for (const nListOneDirection of nodeNeighborByDirectionMapValues) {
              const neighborIndex = nListOneDirection[centerIndex]
              if (neighborIndex !== -1) {
                indexesToUpdate.add(neighborIndex)
              }

              const DO_ONE_MORE_LAYER = true
              if (DO_ONE_MORE_LAYER && neighborIndex !== -1) {
                const centerIndex2 = neighborIndex
                for (const nListOneDirection of nodeNeighborByDirectionMapValues) {
                  const neighborIndex2 = nListOneDirection[centerIndex2]
                  if (neighborIndex2 !== -1) {
                    indexesToUpdate.add(neighborIndex2)
                  }
                }
              }
            }
          }
        }

        // console.log('indexesToUpdate.size', indexesToUpdate.size)
        // if (indexesToUpdate.size > 0) {
        //   const gridWidth = engine.state.width
        //   console.log('indexesToUpdate.values', [...indexesToUpdate.values()].join(', '))
        //   console.log('indexesToUpdate.values', [...indexesToUpdate.values()].reduce((map, index) => {
        //     const position = tileIndexToPositionXY(gridWidth, index)
        //     map[index] = position
        //     return map
        //   }, {} as Record<TileGraphNode['index'], TilePositionXY>))
        // }

        // Update only the affected tiles and their neighbors
        for (const index of indexesToUpdate) {
          map.set(index, make(index))
        }
      }
    })
  }))

  return map
})